// LifeWheelSection.jsx (FULL CLEAN VERSION)
// - Center "Life" hub is DEFAULT on load
// - Clicking center shows global Life explanation in the right panel
// - Clicking any slice shows that domain
// - Removes the JSX errors you currently have (you pasted JS inside <svg>)

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../Style/ProductPage.css";

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, rOuter, startAngle);
  const endOuter = polarToCartesian(cx, cy, rOuter, endAngle);
  const startInner = polarToCartesian(cx, cy, rInner, endAngle);
  const endInner = polarToCartesian(cx, cy, rInner, startAngle);

  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}`,
    "Z",
  ].join(" ");
}

export default function LifeWheelSection() {
  const { t } = useTranslation("product");

  // Domains from i18n
  const items = safeArray(t("wheel.items", { returnObjects: true }));

  // "Life" hub content (global explanation)
  const hub = useMemo(
    () => ({
      id: "life",
      short: "Life",
      name: t("wheel.hub.name"),
      promise: t("wheel.hub.promise"),
      features: safeArray(t("wheel.hub.features", { returnObjects: true })),
      where: safeArray(t("wheel.hub.where", { returnObjects: true })),
      cta: {
        label: t("wheel.hub.cta.label"),
        href: t("wheel.hub.cta.href"),
      },
    }),
    [t]
  );

  // Palette (domain id -> gradient)
  const palette = useMemo(
    () => ({
      time: { a: "rgba(90,150,255,0.55)", b: "rgba(90,150,255,0.18)" },
      finance: { a: "rgba(255,200,90,0.55)", b: "rgba(255,200,90,0.18)" },
      pro: { a: "rgba(140,110,255,0.55)", b: "rgba(140,110,255,0.18)" },
      personal: { a: "rgba(255,120,190,0.50)", b: "rgba(255,120,190,0.16)" },
      social: { a: "rgba(90,220,170,0.50)", b: "rgba(90,220,170,0.16)" },
      eco: { a: "rgba(90,200,120,0.52)", b: "rgba(90,200,120,0.16)" },
      life: { a: "rgba(255,255,255,0.10)", b: "rgba(255,255,255,0.03)" }
    }),
    []
  );

  // DEFAULT: show "Life" hub first
  const [activeId, setActiveId] = useState("life");

  // Active content (hub or a domain)
  const active = useMemo(() => {
    if (activeId === "life") return hub;
    return items.find((x) => x?.id === activeId) || hub;
  }, [items, activeId, hub]);

  // Wheel geometry
  const cx = 260;
  const cy = 260;
  const rOuter = 235;
  const rInner = 140;

  const n = Math.max(1, Math.min(12, items.length || 6));
  const slice = 360 / n;
  const startAt = -90; // top

  // When hub is active: do not fade slices (looks cleaner)
  const fadeOthers = activeId !== "life";

  return (
    <section className="life-section product-wheelSection" id="wheel">
      <div className="life-container">
        <div className="product-sectionHead">
          <div className="product-eyebrow">{t("wheel.eyebrow")}</div>
          <h2 className="product-h2">{t("wheel.title")}</h2>
          <p className="product-lead">{t("wheel.subtitle")}</p>
        </div>

        <div className="life-glass product-wheelBigCard">
          <div className="product-wheelBigGrid">
            {/* BIG SVG */}
            <div className="product-wheelStage" aria-label={t("wheel.a11y")}>
              <svg className="product-wheelSvg" viewBox="0 0 520 520" role="img">
                <defs>
                  <filter id="lifeGlow" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>

                  {/* per-domain gradients */}
                  {items.slice(0, n).map((d) => {
                    const c =
                      palette[d.id] || { a: "rgba(255,255,255,0.30)", b: "rgba(255,255,255,0.08)" };
                    return (
                      <linearGradient key={d.id} id={`grad-${d.id}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={c.a} />
                        <stop offset="100%" stopColor={c.b} />
                      </linearGradient>
                    );
                  })}

                  <radialGradient id="centerGlass" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
                    <stop offset="70%" stopColor="rgba(255,255,255,0.02)" />
                    <stop offset="100%" stopColor="rgba(0,0,0,0.12)" />
                  </radialGradient>
                </defs>

                {/* outer ring */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={rOuter}
                  fill="rgba(255,255,255,0.02)"
                  stroke="rgba(255,255,255,0.10)"
                />

                {/* slices */}
                {items.slice(0, n).map((d, i) => {
                  const a0 = startAt + i * slice + 2;
                  const a1 = startAt + (i + 1) * slice - 2;
                  const path = arcPath(cx, cy, rOuter, rInner, a0, a1);
                  const isActive = d.id === activeId;

                  return (
                    <g key={d.id}>
                      <path
                        d={path}
                        className={`product-wheelSlice ${isActive ? "isActive" : ""}`}
                        fill={`url(#grad-${d.id})`}
                        stroke={isActive ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)"}
                        onClick={() => setActiveId(d.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") setActiveId(d.id);
                        }}
                        style={{
                          filter: isActive ? "url(#lifeGlow)" : "none",
                          opacity: fadeOthers ? (isActive ? 1 : 0.45) : 1
                        }}
                      />

                      {/* label */}
                      {(() => {
                        const mid = (a0 + a1) / 2;
                        const p = polarToCartesian(cx, cy, (rOuter + rInner) / 2, mid);
                        return (
                          <text
                            x={p.x}
                            y={p.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className={`product-wheelText ${isActive ? "isActive" : ""}`}
                            onClick={() => setActiveId(d.id)}
                            style={{ opacity: fadeOthers ? (isActive ? 1 : 0.65) : 0.85 }}
                          >
                            {d.short}
                          </text>
                        );
                      })()}
                    </g>
                  );
                })}

                {/* center clickable hub */}
                <g
                  className={`product-wheelHub ${activeId === "life" ? "isActive" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveId("life")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setActiveId("life");
                  }}
                >
                  <circle
                    cx={cx}
                    cy={cy}
                    r={rInner - 10}
                    fill="url(#centerGlass)"
                    stroke="rgba(255,255,255,0.14)"
                  />

                  {/* "Logo" text (assetless) */}
                  <text x={cx} y={cy - 6} textAnchor="middle" className="product-wheelHubLogo">
                    LIFE
                  </text>
                  <text x={cx} y={cy + 16} textAnchor="middle" className="product-wheelHubSub">
                    {t("wheel.hub.center_hint")}
                  </text>
                </g>

                {/* center dot */}
                <circle cx={cx} cy={cy} r="4" fill="rgba(255,255,255,0.75)" opacity="0.75" />
              </svg>
            </div>

            {/* RIGHT PANEL */}
            <div className="product-wheelDetails">
              <div className="product-badge">
                {activeId === "life" ? t("wheel.hub.badge") : t("wheel.detail_badge")}
              </div>

              <div className="product-wheelDetailTitle">{active.name}</div>
              <div className="product-wheelDetailText">{active.promise}</div>

              <div className="product-wheelMiniTitle">{t("wheel.features_title")}</div>
              <ul className="product-list compact">
                {safeArray(active.features).slice(0, 4).map((x, idx) => (
                  <li key={idx}>{x}</li>
                ))}
              </ul>

              <div className="product-wheelMiniTitle">{t("wheel.where_title")}</div>
              <div className="product-whereRow">
                {safeArray(active.where).map((w, idx) => (
                  <span key={idx} className="product-chip">
                    {w}
                  </span>
                ))}
              </div>

              <div className="product-ctaRow">
                <a className="product-ctaSecondary" href={active?.cta?.href || "#"}>
                  {active?.cta?.label || t("wheel.cta_default")}
                </a>
              </div>

              <div className="product-wheelHint">{t("wheel.hint")}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
